import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { fromJS, Map } from 'immutable';
import classNames from 'classnames';

import Popup from '../../../../../components/helpers/Popup';
import Button from '../../../../../components/helpers/Button';
import Select from '../../../../../components/helpers/Select';
import FormGroup from '../../../../../components/helpers/FormGroup';
import FieldError from '../../../../../components/helpers/FieldError';
import PdfViewerPopup from '../../../../../components/helpers/PdfViewerPopup';
import { InputProps } from '../../../../../components/helpers/AutoComplete';
import AutoComplete from '../../../../AutoComplete';
import { findDirectors } from '../../../../../actions/company/companyTarget';
import { unwrap } from '../../../../../utils/ChangeSpy';
import { loadNdaDirector, previewNda, saveNda, sendNda } from '../../../../../actions/company/target/ndaReport';
import { showError, showInformation } from '../../../../../utils/MessagePopup';
import downloadFile from '../../../../../utils/downloadFile';
import { usePopupContext } from '../../../../../contexts/PopupContext';
import NdaSigners from './NdaSigners';

import style from './style.module.css';

const DIRECTOR_SUGGEST_ID = 'suggestDirectorId';
const NDA_TYPE_HARVEY = 'HarveyNDA';
const NDA_TYPE_CLIENT = 'ClientSpecificNDA';
const HARVEY_SIGNS_FOR = 'Harvey & Company LLC';
const TARGET_SIGNING_ORDER = 1;
const header = 'You are generating a new NDA';
const warningMessage = '(This company already has an existing NDA)';
const sentMessage = 'NDA has been sent via DocuSign';
const savedMessage = 'NDA has been saved';
const ERROR_FIELDS = ['directorId', 'buyerId', 'contactId', 'contactEmailId'];
const FORMAT_DOCX = 'docx';
const FORMAT_PDF = 'pdf';
const previewHeader = 'NDA Preview';
const downloadErrorMessage = 'Failed to download the NDA file';

/**
 * Returns the primary target contact (the one with the lowest priority).
 *
 * @param {List} contacts Target contacts.
 * @returns {Map|undefined} Primary contact.
 */
const getPrimaryContact = contacts => contacts.minBy(contact => unwrap(contact.getIn(['pivot', 'priority'])) || 0);

/**
 * Returns contact's non-empty emails.
 *
 * @param {Map} [contact] Target contact.
 * @returns {{ id: number, email: string }[]} Emails.
 */
const getContactEmails = contact =>
  contact
    ? contact
        .get('emails')
        .map(email => ({ id: email.get('id'), email: unwrap(email.get('email')) }))
        .filter(({ email }) => Boolean(email))
        .toArray()
    : [];

/**
 * Returns the initial target signer contact: the last NDA one if it still exists, otherwise the primary contact.
 *
 * @param {List} contacts Target contacts.
 * @param {Map|null} [lastNda] Company last NDA.
 * @returns {Map|undefined} Contact.
 */
const getInitialContact = (contacts, lastNda) =>
  (lastNda && contacts.find(contact => contact.get('id') === lastNda.get('contactId'))) || getPrimaryContact(contacts);

/**
 * Returns the initial target signer email id: the last NDA one if the contact still has it, otherwise the first one.
 *
 * @param {List} contacts Target contacts.
 * @param {Map|null} [lastNda] Company last NDA.
 * @returns {number|undefined} Email id.
 */
const getInitialEmailId = (contacts, lastNda) => {
  const emails = getContactEmails(getInitialContact(contacts, lastNda));
  const lastEmail = lastNda && emails.find(({ id }) => id === lastNda.get('contactEmailId'));

  return (lastEmail || emails[0])?.id;
};

/**
 * Returns parsed API field errors and the rest messages.
 *
 * @param {object} [errors] API errors by field.
 * @returns {{ fieldErrors: object, messages: string[] }} Field errors and other messages.
 */
const parseApiErrors = (errors = {}) =>
  Object.entries(errors).reduce(
    (result, [field, messages]) => {
      const message = [].concat(messages).join(' ');

      if (ERROR_FIELDS.includes(field)) {
        return { ...result, fieldErrors: { ...result.fieldErrors, [field]: message } };
      }

      return { ...result, messages: [...result.messages, message || field] };
    },
    { fieldErrors: {}, messages: [] },
  );

/**
 * NDA request popup component.
 */
const NdaRequestPopupContainer = ({
  companyInfo,
  lastNda,
  legalName,
  findDirectors,
  loadNdaDirector,
  previewNda,
  saveNda,
  sendNda,
  popup,
}) => {
  const { openPopup, closePopup } = usePopupContext();
  const [suggestDirectorValue, setSuggestDirectorValue] = useState(() =>
    lastNda ? lastNda.get('moduleFullName') : companyInfo.getIn(['suggestDirectorData', 'name']),
  );
  const [suggestDirectorId, setSuggestDirectorId] = useState(() =>
    lastNda ? lastNda.get('moduleId') : companyInfo.getIn(['suggestDirectorData', 'id']),
  );
  const [director, setDirector] = useState(null);
  const [selectedBuyer, setSelectedBuyer] = useState(
    () => lastNda?.get('buyerId') ?? companyInfo.getIn(['buyers', 0, 'buyerId']),
  );
  const [ndaType, setNdaType] = useState(() => lastNda?.get('type') ?? NDA_TYPE_HARVEY);
  const [contactId, setContactId] = useState(() => getInitialContact(companyInfo.get('contacts'), lastNda)?.get('id'));
  const [contactEmailId, setContactEmailId] = useState(() => getInitialEmailId(companyInfo.get('contacts'), lastNda));
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [pdfPreview, setPdfPreview] = useState(null);

  const companyId = parseInt(popup.getIn(['props', 'companyId'], null), 10);

  const contacts = companyInfo.get('contacts');
  const selectedContact = contacts.find(contact => String(contact.get('id')) === String(contactId));
  const selectedEmail = useMemo(
    () => getContactEmails(selectedContact).find(({ id }) => String(id) === String(contactEmailId)),
    [selectedContact, contactEmailId],
  );

  /**
   * Resets errors of the given fields.
   *
   * @param {string[]} fields Field names.
   */
  const resetErrors = useCallback(fields => {
    setErrors(prev =>
      fields.some(field => prev[field])
        ? Object.fromEntries(Object.entries(prev).filter(([field]) => !fields.includes(field)))
        : prev,
    );
  }, []);

  useEffect(() => {
    setDirector(null);

    if (!suggestDirectorId) return undefined;

    let isActual = true;

    loadNdaDirector(suggestDirectorId, ({ response }) => {
      if (isActual) setDirector(response.data);
    });

    return () => {
      isActual = false;
    };
  }, [suggestDirectorId, loadNdaDirector]);

  /** Director suggests handler. */
  const handleDirectorSuggestChange = useCallback(
    ({ name, value }) => {
      if (name === 'directorText') {
        setSuggestDirectorValue(value);
        setSuggestDirectorId('');
      } else if (name === DIRECTOR_SUGGEST_ID) {
        setSuggestDirectorId(value);
      }
      resetErrors(['directorId']);
    },
    [resetErrors],
  );

  /** Buyer change handler. */
  const handleBuyerChange = useCallback(
    ({ target: { value } }) => {
      setSelectedBuyer(value);
      resetErrors(['buyerId']);
    },
    [resetErrors],
  );

  /** Nda radio control change handler. */
  const handleNdaTypeChange = useCallback(
    event => {
      setNdaType(event.target.value);
      resetErrors(['buyerId']);
    },
    [resetErrors],
  );

  /** Target signer contact change handler. */
  const handleContactChange = useCallback(
    ({ target: { value } }) => {
      const contact = contacts.find(item => String(item.get('id')) === value);

      setContactId(value);
      setContactEmailId(getContactEmails(contact)[0]?.id);
      resetErrors(['contactId', 'contactEmailId']);
    },
    [contacts, resetErrors],
  );

  /** Target signer email change handler. */
  const handleEmailChange = useCallback(
    ({ target: { value } }) => {
      setContactEmailId(value);
      resetErrors(['contactEmailId']);
    },
    [resetErrors],
  );

  const contactOptions = useMemo(
    () => contacts.map(contact => ({ value: contact.get('id'), name: unwrap(contact.get('fullName')) })).toArray(),
    [contacts],
  );

  const emailOptions = useMemo(
    () => getContactEmails(selectedContact).map(({ id, email }) => ({ value: id, name: email })),
    [selectedContact],
  );

  const targetSigner = useMemo(
    () => ({
      signingOrder: TARGET_SIGNING_ORDER,
      signsFor: legalName,
      name: selectedContact ? unwrap(selectedContact.get('fullName')) : '',
      title: selectedContact ? unwrap(selectedContact.getIn(['pivot', 'title'])) : '',
      email: selectedEmail?.email,
    }),
    [legalName, selectedContact, selectedEmail],
  );

  const harveySigner = useMemo(
    () => ({
      signingOrder: TARGET_SIGNING_ORDER + 1,
      signsFor: HARVEY_SIGNS_FOR,
      name: director ? [director.firstName, director.lastName].filter(Boolean).join(' ') : '',
      title: director ? director.title : '',
      email: director ? director.email : '',
    }),
    [director],
  );

  useEffect(
    () => () => {
      if (pdfPreview) URL.revokeObjectURL(pdfPreview.url);
    },
    [pdfPreview],
  );

  /**
   * Download NDA success handler: downloads the generated file.
   *
   * @param {object} payload Server payload data.
   * @param {object} payload.response Server success response.
   */
  const handleDownloadSuccess = useCallback(
    ({
      response: {
        data: { fileName, fileUrl, error },
      },
    }) => {
      if (error) {
        setLoading(false);
        showError(openPopup, [error]);

        return;
      }

      const handleDownloadError = () => showError(openPopup, [downloadErrorMessage]);

      // Not async: CALL_API skips async callbacks (isFunction checks for '[object Function]').
      downloadFile({ url: fileUrl, name: fileName, noAuth: true, onError: handleDownloadError })
        .catch(handleDownloadError)
        .finally(() => setLoading(false));
    },
    [openPopup],
  );

  /**
   * Returns Preview NDA success handler: loads the generated PDF and opens it in the viewer.
   *
   * @param {number} ndaId Saved NDA id.
   * @returns {Function} Success handler.
   */
  const getPreviewSuccessHandler = useCallback(
    ndaId => ({
      response: {
        data: { fileUrl, error },
      },
    }) => {
      if (error) {
        setLoading(false);
        showError(openPopup, [error]);

        return;
      }

      // Not async: CALL_API skips async callbacks (isFunction checks for '[object Function]').
      fetch(fileUrl)
        .then(response => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);

          return response.blob();
        })
        .then(blob => {
          const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));

          setPdfPreview({ ndaId, url });
        })
        .catch(() => showError(openPopup, [downloadErrorMessage]))
        .finally(() => setLoading(false));
    },
    [openPopup],
  );

  /** Send via DocuSign success handler. */
  const handleSendSuccess = useCallback(() => {
    closePopup();
    showInformation(openPopup, sentMessage);
  }, [closePopup, openPopup]);

  /** Save success handler. */
  const handleSaveSuccess = useCallback(() => {
    closePopup();
    showInformation(openPopup, savedMessage);
  }, [closePopup, openPopup]);

  /**
   * Save / Preview NDA / Send via DocuSign error handler.
   *
   * @param {object} payload Server payload data.
   * @param {object} payload.responseBody Server error response.
   */
  const handleRequestError = useCallback(
    ({ responseBody }) => {
      const { fieldErrors, messages } = parseApiErrors(responseBody?.errors);

      setLoading(false);

      // The message popup replaces the current one, so field errors are shown in place instead.
      if (Object.keys(fieldErrors).length) {
        setErrors(fieldErrors);

        return;
      }

      showError(openPopup, messages.length ? messages : [responseBody?.message || 'An error occurred']);
    },
    [openPopup],
  );

  /**
   * Validates the form and saves the NDA.
   *
   * @param {Function} afterSuccess Callback after the NDA is saved.
   */
  const submitNda = useCallback(
    afterSuccess => {
      const isNdaClient = ndaType === NDA_TYPE_CLIENT;
      const validationErrors = {};

      if (!suggestDirectorId) {
        validationErrors.directorId = 'Director is required';
      }

      if (isNdaClient && !selectedBuyer) {
        validationErrors.buyerId = 'Buyer is required';
      }

      if (!selectedContact) {
        validationErrors.contactId = 'Contact is required';
      }

      if (!selectedEmail) {
        validationErrors.contactEmailId = 'Email is required';
      }

      setErrors(validationErrors);

      if (Object.keys(validationErrors).length) return;

      const body = {
        companyId,
        moduleId: parseInt(suggestDirectorId, 10),
        type: ndaType,
        contactId: selectedContact.get('id'),
        contactEmailId: selectedEmail.id,
      };

      if (isNdaClient) {
        body.buyerId = parseInt(selectedBuyer, 10);
      }

      setLoading(true);
      saveNda(body, afterSuccess, handleRequestError);
    },
    [companyId, ndaType, suggestDirectorId, selectedBuyer, selectedContact, selectedEmail, saveNda, handleRequestError],
  );

  /**
   * Downloads the saved NDA as docx.
   *
   * @param {number} ndaId Saved NDA id.
   */
  const downloadNda = useCallback(
    ndaId => previewNda(companyId, ndaId, FORMAT_DOCX, handleDownloadSuccess, handleRequestError),
    [companyId, previewNda, handleDownloadSuccess, handleRequestError],
  );

  /** Preview NDA handler. */
  const handlePreview = useCallback(() => {
    submitNda(({ response }) =>
      previewNda(companyId, response.id, FORMAT_PDF, getPreviewSuccessHandler(response.id), handleRequestError),
    );
  }, [companyId, submitNda, previewNda, getPreviewSuccessHandler, handleRequestError]);

  /** Download NDA handler. */
  const handleDownload = useCallback(() => {
    submitNda(({ response }) => downloadNda(response.id));
  }, [submitNda, downloadNda]);

  /** PDF viewer Download handler: downloads the previewed NDA without saving it again. */
  const handlePdfDownload = useCallback(() => {
    setLoading(true);
    downloadNda(pdfPreview.ndaId);
  }, [downloadNda, pdfPreview]);

  /** PDF viewer close handler. */
  const handlePdfPreviewClose = useCallback(() => setPdfPreview(null), []);

  /** Send via DocuSign handler. */
  const handleSend = useCallback(() => {
    submitNda(({ response }) => sendNda(companyId, response.id, handleSendSuccess, handleRequestError));
  }, [companyId, submitNda, sendNda, handleSendSuccess, handleRequestError]);

  /** Save handler. */
  const handleSave = useCallback(() => {
    submitNda(handleSaveSuccess);
  }, [submitNda, handleSaveSuccess]);

  /**
   * Convert the buyer list to a style-friendly list for autocomplete.
   *
   * @param {List} buyers Target buyers list.
   * @returns {List} Buyers formatted buyers list.
   */
  const convertBuyersToSelectFormat = useCallback(
    buyers =>
      buyers.map(buyer => fromJS({ value: buyer.get('buyerId'), name: unwrap(buyer.get('dsplBuyerLegalName')) })),
    [],
  );

  /** Returns form validity boolean value. */
  const isValid = useCallback(() => {
    const isHarveyType = ndaType === NDA_TYPE_HARVEY;
    const isClientType = ndaType === NDA_TYPE_CLIENT;
    const ndaValid = (!!isClientType && selectedBuyer) || isHarveyType;
    const directorValid = suggestDirectorValue && suggestDirectorId;

    return directorValid && ndaValid && !loading;
  }, [suggestDirectorValue, suggestDirectorId, ndaType, selectedBuyer, loading]);

  const isNdaActive = companyInfo.getIn(['nda', 'isActive'], null);
  const directorSuggests = companyInfo.get('suggests');
  const buyers = convertBuyersToSelectFormat(companyInfo.get('buyers'));
  const canSubmit = isValid();

  const subheader = isNdaActive && warningMessage;
  const footer = (
    <>
      <Button key="preview" disabled={!canSubmit} onClick={handlePreview} type="button">
        Preview NDA
      </Button>
      <Button key="download" disabled={!canSubmit} onClick={handleDownload} type="button">
        Download
      </Button>
      <Button key="send" disabled={loading} onClick={handleSend} type="button">
        Send via DocuSign
      </Button>
      <Button key="save" className="btn-primary" disabled={loading} onClick={handleSave} type="button">
        Save
      </Button>
    </>
  );

  const renderTargetBuyers = ndaType === NDA_TYPE_CLIENT && (
    <FormGroup className={classNames(style.field, { 'has-error': errors.buyerId })}>
      <Select
        label="Buyer"
        name="buyer"
        onChange={handleBuyerChange}
        options={buyers}
        selectClassName="input-sm"
        value={selectedBuyer}
        showLabel
      />
      <FieldError message={errors.buyerId} />
    </FormGroup>
  );

  // The viewer is a sibling, not a child: React events from its portal would bubble into this popup's Draggable.
  return (
    <>
      <Popup
        className={style.host}
        footer={footer}
        header={header}
        id="ndaRequestPopup"
        loading={loading}
        subheader={subheader}
        subheaderClassName={style.subheader}
      >
        <div className={style.body}>
          <FormGroup className={classNames(style.field, { 'has-error': errors.directorId })}>
            <label className="field-label" htmlFor={DIRECTOR_SUGGEST_ID}>
              Director
            </label>
            <InputProps className="form-control input-sm" placeholder="Director">
              <AutoComplete
                highlightFirst={false}
                idName={DIRECTOR_SUGGEST_ID}
                idValue={suggestDirectorId}
                name="director"
                onChange={handleDirectorSuggestChange}
                onFind={findDirectors}
                suggestions={directorSuggests}
                suggestionsName="suggests"
                value={suggestDirectorValue}
                valueName="directorText"
              />
            </InputProps>
            <FieldError message={errors.directorId} />
          </FormGroup>
          <div className={style.types}>
            <div className="radio-wrap">
              <input
                checked={ndaType === NDA_TYPE_HARVEY}
                id={`ndaType_${NDA_TYPE_HARVEY}`}
                name="ndaType"
                onChange={handleNdaTypeChange}
                type="radio"
                value={NDA_TYPE_HARVEY}
              />
              <label htmlFor={`ndaType_${NDA_TYPE_HARVEY}`}>Harvey NDA</label>
            </div>
            <div className="radio-wrap">
              <input
                checked={ndaType === NDA_TYPE_CLIENT}
                id={`ndaType_${NDA_TYPE_CLIENT}`}
                name="ndaType"
                onChange={handleNdaTypeChange}
                type="radio"
                value={NDA_TYPE_CLIENT}
              />
              <label htmlFor={`ndaType_${NDA_TYPE_CLIENT}`}>Client Specific NDA</label>
            </div>
          </div>
          {renderTargetBuyers}
          <NdaSigners
            contactEmailId={contactEmailId}
            contactId={contactId}
            contactOptions={contactOptions}
            emailOptions={emailOptions}
            errors={errors}
            harveySigner={harveySigner}
            onContactChange={handleContactChange}
            onEmailChange={handleEmailChange}
            targetSigner={targetSigner}
          />
        </div>
      </Popup>
      {pdfPreview && (
        <PdfViewerPopup
          header={previewHeader}
          loading={loading}
          onClose={handlePdfPreviewClose}
          onDownload={handlePdfDownload}
          url={pdfPreview.url}
        />
      )}
    </>
  );
};

NdaRequestPopupContainer.propTypes = {
  companyInfo: PropTypes.instanceOf(Map).isRequired,
  findDirectors: PropTypes.func.isRequired,
  lastNda: PropTypes.instanceOf(Map),
  legalName: PropTypes.string,
  loadNdaDirector: PropTypes.func.isRequired,
  popup: PropTypes.object.isRequired,
  previewNda: PropTypes.func.isRequired,
  saveNda: PropTypes.func.isRequired,
  sendNda: PropTypes.func.isRequired,
};

const mapStateToProps = (state, props) => ({
  ...props,
  companyInfo: state.targetCompany.target,
  lastNda: state.targetCompany.info.getIn(['info', 'targetLastNda']),
  legalName: unwrap(state.targetCompany.info.getIn(['info', 'legalName'])),
});

export default connect(mapStateToProps, {
  findDirectors,
  loadNdaDirector,
  previewNda,
  saveNda,
  sendNda,
})(memo(NdaRequestPopupContainer));
